import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Flex, Box, Collapsible } from '@chakra-ui/react';
import Header from '../../sections/header';
import Footer from '../../sections/footer';
import CSCLogo from '../../ui/brandlogos/_blackcsc_logo_cr.png';

import SearchImpactReports from '../../sections/impactreport/search';

export default function ImpactReportLayout(props) {
  const { hash, pathname } = useLocation();

  // Chrome's automatic scroll restoration fires asynchronously after pushState
  // and resets the scroll position to 0 for new history entries, overriding
  // scrollIntoView. Setting 'manual' here covers the initial page load path
  // (direct URL access / S3 404 → index.html boot). The search dialog click
  // handler also sets it before navigate() to cover the cross-page click path.
  useEffect(() => {
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = prev; };
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    // Push the scroll into a macrotask so Chrome completes its layout pass
    // for newly rendered page content before we call scrollIntoView.
    // Running immediately (in the microtask/effect flush) causes Chrome to
    // compute element positions before images and complex layouts have
    // settled, resulting in a scroll to the wrong offset. Firefox and Edge
    // are more forgiving here. The setTimeout(fn, 0) also ensures any
    // concurrent work from closing the dialog (overflow:hidden removal) and
    // React Router's navigation machinery finishes first.
    let timerId = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Element not yet in DOM (deferred-script initial load path).
        // Retry once after a short pause.
        timerId = setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    }, 0);
    return () => clearTimeout(timerId);
  }, [hash, pathname]);

  return (
    <Flex direction="column" {...props} maxW={'1640px'} mx={'auto'}>
      <Header maxW='{1640px}' />
        { props.reporthero ? 
          (<div className='report-header'>
            {props.reporthero}
          </div>)
          : null
        }
        { props.abovethereport ?
          (<div className='report-above'>
            {props.abovethereport}
          </div>)
          : null
        } 
        <section className='sideBar'>
          <div className='sideBar-container'>
            <div className='sideBar-spacer'></div>
            <div className='sideBar-page'>
              <aside className='sideBar-sidebar'>
                <div className='sideBar-sidebar__logo'>
                  <a href='/corporate-responsibility/impact/2024'>
                    <img src={CSCLogo} alt='' />
                  </a>
                </div>
                <div className='sideBar-sidebar__title'>
                  <span>{props.reportTitle}</span>
                </div>
                <Box className='sideBar-sidebar__nav' display={{ base: 'none', lg: 'block' }}>
                  {props.sidebar}
                </Box>
                {props.pdfUrl ? (
                  <div className='sideBar-sidebar__pdf-download'>
                    <a href={props.pdfUrl} download>
                      Download Full Report
                    </a>
                  </div>
                ) : null}
                <MobileMenu sidebar={props.sidebar} />
              </aside>
              <main className='sideBar-main'>
                {props.mainhero ?
                  (<div className='sideBar-main__hero'>
                    {props.mainhero}
                  </div>)
                  : null
                }
                <div className='sideBar-main__contents'>
                  <article className='sideBar-Main__article'>
                    <header className='sideBar-Main__article-header'>
                      { props.subheading ?
                        (<div className='sideBar-Main__article-subheading'>
                          {props.subheading}
                        </div>)
                        : null
                      }
                      <h1 className='sideBar-Main__article-heading'>
                        {props.heading}
                      </h1>
                    </header>
                    <div id='toc-content' className='sideBar-Main__article-content flow'>
                      {props.children}
                      <div className='container' style={{ marginTop: '4dvh'}}>
                        <p style={{ fontSize: 'var(--step--2)', fontStyle: 'italic', maxW: '100%' }}>Last updated: July 30, 2025</p>
                      </div>
                    </div>
                  </article>
                </div>
              </main>
            </div>
          </div>
        </section>
      <Footer />
      <SearchImpactReports />
    </Flex>
  );
}

const MobileMenu = (props) => {
  return (
    <Box className='sideBar-sidebar__nav'  display={{ base: 'flex', lg: 'none' }}>
      <Collapsible.Root>
        <Collapsible.Trigger 
          paddingBlockStart={'var(--space-xs)'} 
          textTransform={'uppercase'} 
          fontWeight={'bold'}
          color={'gray.600'}
          cursor={'pointer'}
          fontSize={'var(--step--1)'}
        >
          Toggle Navigation
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Box paddingX={'var(--space-s)'} paddingBlockEnd={'var(--space-s)'}>
            {props.sidebar}
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
};
